// Tabs and side nav (STYLE 5). A tab is a link to a sub-route with
// aria-current="page", never local state.
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";

export type TabItem = { to: string; label: ReactNode; count?: number; key?: string; end?: boolean };

/** Current when the path equals `to` (or starts with it, unless `end`); the query string counts when `to` has one. */
export function isCurrentTab(item: Pick<TabItem, "to" | "end">, pathname: string, search: string, all: Pick<TabItem, "to">[] = []): boolean {
  const [path, query] = item.to.split("?");
  if (query !== undefined) {
    const want = new URLSearchParams(query);
    const have = new URLSearchParams(search);
    return pathname === path && [...want.entries()].every(([k, v]) => have.get(k) === v);
  }
  if (pathname === path) {
    // A tab without a query is not current when a sibling with a matching query is.
    return !all.some((o) => o.to.includes("?") && isCurrentTab(o, pathname, search));
  }
  if (item.end) return false;
  // The longest matching prefix wins, so "/admin/tors" does not light up on "/admin/tors-x".
  const longer = all.some((o) => o.to !== item.to && o.to.startsWith(path) && pathname.startsWith(o.to.split("?")[0]));
  return !longer && pathname.startsWith(`${path}/`);
}

/** The system's count badge after a tab's name; screen readers hear "Name (3)". */
export function TabCount({ count }: { count: ReactNode }) {
  return (
    <>
      <span className="tabs__count" aria-hidden="true">
        {count}
      </span>
      <span className="visually-hidden"> ({count})</span>
    </>
  );
}

export function Tabs({ items, label }: { items: TabItem[]; label: string }) {
  const { pathname, search } = useLocation();
  return (
    <nav className="tabs" aria-label={label}>
      {items.map((it) => {
        const current = isCurrentTab(it, pathname, search, items);
        return (
          <Link key={it.key ?? it.to} className="tabs__tab" to={it.to} aria-current={current ? "page" : undefined}>
            {it.label}
            {it.count !== undefined ? <TabCount count={it.count} /> : null}
          </Link>
        );
      })}
    </nav>
  );
}

/** `replace`: the click replaces the history entry (a section picked within one page), so Back leaves the page. */
export type SideNavItem = { to: string; label: ReactNode; sub?: ReactNode; key?: string; replace?: boolean };

/** `foot`: a line under the items, inside the card (the onboarding menu's "23 items to review · See the summary"). */
export function SideNav({ items, label, current, foot }: { items: SideNavItem[]; label: string; current?: string; foot?: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <nav className="card side-list" aria-label={label}>
      <ol>
        {items.map((it) => {
          const isCurrent = current ? it.key === current || it.to === current : pathname === it.to.split("?")[0];
          return (
            <li key={it.key ?? it.to}>
              <Link className="side-list__item" to={it.to} replace={it.replace} aria-current={isCurrent ? "page" : undefined}>
                {it.label}
                {it.sub ? (
                  <>
                    {" "}
                    <span className="side-list__count">{it.sub}</span>
                  </>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ol>
      {/* DS-DEBT: SideList foot (a summary line under the items) */}
      {foot ? <div className="stack">{foot}</div> : null}
    </nav>
  );
}
